import { useRef, useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import viinexService from '@/services/viinex';
import { addTimestampToUrl } from '@/utils/camera';
import { I18nNamespace } from '@/lib/i18n-namespaces';
import { LiveThumbnail } from './LiveThumbnail';

const FADE_DURATION_MILLISECONDS = 150;

interface CameraThumbnailProps extends Omit<
    React.ImgHTMLAttributes<HTMLImageElement>,
    'src' | 'onError'
> {
    sourceId: string;
    refreshInterval?: number;
    alt: string;
    liveOnHover?: boolean;
    disabled?: boolean;
    isHovered?: boolean;
    onConnectionChange?: (isOnline: boolean) => void;
}

export function CameraThumbnail({
    sourceId,
    refreshInterval = 3000,
    alt,
    className,
    liveOnHover = false,
    disabled = false,
    isHovered: isHoveredProp,
    onConnectionChange,
    ...props
}: CameraThumbnailProps) {
    const { t } = useTranslation(I18nNamespace.CAMERAS);
    const containerRef = useRef<HTMLDivElement>(null);
    const isRunningRef = useRef(false);
    const isHoveredRef = useRef(false);
    const timeoutRef = useRef<NodeJS.Timeout | null>(null);
    const previousSnapshotPollingStoppedRef = useRef(false);
    const lastRefreshTimeRef = useRef<number>(0);
    const [snapshotUrl, setSnapshotUrl] = useState<string | null>(null);
    const [error, setError] = useState(false);
    const [loading, setLoading] = useState(true);
    const [isThumbnailHovered, setIsThumbnailHovered] = useState(false);
    const [hlsError, setHlsError] = useState(false);
    const [hlsActive, setHlsActive] = useState(false);
    const [showOverlay, setShowOverlay] = useState(false);
    const [overlayDuration, setOverlayDuration] = useState(
        FADE_DURATION_MILLISECONDS,
    );
    const [dimensions, setDimensions] = useState<{
        width: number;
        height: number;
    } | null>(null);

    const isHovered = isHoveredProp ?? isThumbnailHovered;
    const snapshotPollingStopped = liveOnHover && isHovered;
    const showHls = hlsActive && !hlsError;

    useEffect(() => {
        onConnectionChange?.(!error && !loading);
    }, [error, loading, onConnectionChange]);

    useEffect(() => {
        if (snapshotPollingStopped && timeoutRef.current) {
            clearTimeout(timeoutRef.current);
            timeoutRef.current = null;
        }
    }, [snapshotPollingStopped]);

    useEffect(() => {
        if (!liveOnHover) return;

        if (isHovered && !hlsError && !disabled) {
            setOverlayDuration(FADE_DURATION_MILLISECONDS);
            setShowOverlay(true);
        } else {
            setOverlayDuration(0);
            setShowOverlay(false);
            setHlsActive(false);
        }
    }, [isHovered, liveOnHover, hlsError, disabled]);

    const handleOverlayTransitionEnd = useCallback(
        (e: React.TransitionEvent) => {
            if (e.propertyName !== 'opacity') return;

            if (showOverlay && !hlsActive && isHoveredRef.current) {
                setHlsActive(true);
            }
        },
        [showOverlay, hlsActive],
    );

    const handleHlsReady = useCallback(() => {
        setOverlayDuration(FADE_DURATION_MILLISECONDS);
        setShowOverlay(false);
    }, []);

    useEffect(() => {
        if (containerRef.current) {
            const { width, height } =
                containerRef.current.getBoundingClientRect();
            setDimensions({
                width: Math.round(width),
                height: Math.round(height),
            });
        }
    }, []);

    const thumbnailUrl = viinexService.getCameraSnapshotUrl(sourceId, {
        width: dimensions?.width,
        height: dimensions?.height,
    });

    const snapshotPollingStoppedRef = useRef(false);
    snapshotPollingStoppedRef.current = snapshotPollingStopped;

    const refreshSnapshot = useCallback(() => {
        if (!isRunningRef.current || !dimensions) return;
        if (snapshotPollingStoppedRef.current) return;

        setSnapshotUrl(addTimestampToUrl(thumbnailUrl));
    }, [thumbnailUrl, dimensions]);

    useEffect(() => {
        if (
            previousSnapshotPollingStoppedRef.current &&
            !snapshotPollingStopped
        ) {
            refreshSnapshot();
        }
        previousSnapshotPollingStoppedRef.current = snapshotPollingStopped;
    }, [snapshotPollingStopped, refreshSnapshot]);

    const handleImageLoad = useCallback(() => {
        if (snapshotPollingStoppedRef.current) return;

        setLoading(false);
        setError(false);
        lastRefreshTimeRef.current = Date.now();

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (isRunningRef.current && isHoveredRef.current) {
            timeoutRef.current = setTimeout(refreshSnapshot, refreshInterval);
        }
    }, [refreshSnapshot, refreshInterval]);

    const handleImageError = useCallback(() => {
        if (snapshotPollingStoppedRef.current) return;

        console.warn('Camera snapshot failed:', sourceId);
        setError(true);
        setLoading(false);

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        if (isRunningRef.current && isHoveredRef.current) {
            timeoutRef.current = setTimeout(refreshSnapshot, refreshInterval);
        }
    }, [sourceId, refreshSnapshot, refreshInterval]);

    useEffect(() => {
        if (!refreshInterval) return;

        isRunningRef.current = true;
        setError(false);
        setLoading(true);

        refreshSnapshot();

        return () => {
            isRunningRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            setSnapshotUrl(null);
        };
    }, [refreshSnapshot, refreshInterval]);

    useEffect(() => {
        isHoveredRef.current = isHovered;

        if (isHovered && !snapshotPollingStopped) {
            const now = Date.now();
            const timeSinceLastRefresh = now - lastRefreshTimeRef.current;

            if (timeSinceLastRefresh >= refreshInterval) {
                lastRefreshTimeRef.current = now;
                refreshSnapshot();
            }
        }
    }, [isHovered, refreshSnapshot, refreshInterval, snapshotPollingStopped]);

    return (
        <div
            ref={containerRef}
            className="relative w-full h-full"
            onMouseEnter={() => setIsThumbnailHovered(true)}
            onMouseLeave={() => setIsThumbnailHovered(false)}
        >
            {snapshotUrl && (
                <img
                    src={snapshotUrl}
                    alt={alt}
                    crossOrigin="use-credentials"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                    className={`pointer-events-none w-full h-full object-cover ${loading || error ? 'opacity-0' : 'opacity-100'} ${className || ''}`}
                    {...props}
                />
            )}
            {loading && (
                <div className="pointer-events-none absolute inset-0 bg-grey-700 animate-pulse" />
            )}
            {error && (
                <div className="pointer-events-none absolute inset-0 bg-grey-700 flex items-center justify-center">
                    <span className="text-white-100 text-xs">
                        {t('offline')}
                    </span>
                </div>
            )}
            {showHls && (
                <LiveThumbnail
                    sourceId={sourceId}
                    isHovered={isHovered}
                    onReady={handleHlsReady}
                    onError={() => setHlsError(true)}
                />
            )}
            <div
                className={`absolute inset-0 bg-black pointer-events-none transition-opacity ${
                    showOverlay ? 'opacity-100' : 'opacity-0'
                } ${overlayDuration === 0 ? 'duration-0' : 'duration-150'}`}
                onTransitionEnd={handleOverlayTransitionEnd}
            />
        </div>
    );
}
